import { ICONS } from '@/pages/investor/events';
import { Box, Flex, font, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { COLORS } from '@lemonade-hq/tokens';
import { rgba } from 'polished';
import styled, { createGlobalStyle } from 'styled-components';
import { Particles } from './Particles';

const GlobalStyle = createGlobalStyle`
  @property --angle {
    syntax: '<angle>';
    inherits: false;
    initial-value: 0deg;
  }

  @keyframes gradientRotate {
    to {
      --angle: 360deg;
    }
  }
`;

const EvenFeedCardContainer = styled(Flex)<{ width?: string; hasUpcomingEvent?: boolean }>`
  ${font('main', { fontSize: '1.6rem', lineHeight: '130%', fontWeight: 400 })}
  border: 1px solid ${themedColor('primary')};
  border-radius: 10px;
  padding: 20px;
  background: ${themedColor('tutu')};
  color: ${themedColor('primary')};
  box-shadow: 0px 6px 20px -4px ${rgba(COLORS.pink, 0.15)};
  align-items: stretch;
  width: 100%;
  max-width: 100%;
  cursor: pointer;
  transition: all ease 0.2s;

  &:hover {
    background: #ffebf5;
    box-shadow: 0px 6px 20px -4px ${rgba(COLORS.pink, 0.15)};
    border-color: ${themedColor('darkPink')};
  }

  h3 {
    ${font('main', { fontSize: '1.2rem', lineHeight: '1.6rem', fontWeight: 700 })}
    color: ${themedColor('darkerPink')};
    text-transform: uppercase;
    margin-bottom: 6px;
  }

  ${MEDIA_QUERIES.tabletAndDesktop`
    width: ${props => props.width ?? '100%'};
    max-width: 178px;
    font-size: 1.4rem;
    gap: ${props => (props.hasUpcomingEvent ? '12px' : '28px')};
  `}
`;

const SpecialEvenFeedCardContainer = styled(Flex)<{ width?: string; hasUpcomingEvent?: boolean }>`
  ${font('main', { fontSize: '1.6rem', lineHeight: '130%', fontWeight: 400 })}
  border-radius: 10px;
  padding: 20px;
  background: ${themedColor('tutu')};
  color: ${themedColor('primary')};
  align-items: stretch;
  width: 100%;
  cursor: pointer;
  transition: all ease 0.2s;
  position: relative;

  &:hover {
    background: #ffebf5;
    border-color: ${themedColor('darkPink')};
  }

  h3 {
    ${font('main', { fontSize: '1.2rem', lineHeight: '1.6rem', fontWeight: 700 })}
    color: ${themedColor('darkerPink')};
    text-transform: uppercase;
    margin-bottom: 6px;
  }

  ${MEDIA_QUERIES.tabletAndDesktop`
    width: ${props => props.width ?? '100%'};
    font-size: 1.4rem;
    gap: ${props => (props.hasUpcomingEvent ? '12px' : '28px')};
  `}
`;

type EvenFeedCardProps = React.PropsWithChildren<React.ComponentProps<'div'>> & {
  title: string;
  width?: string;
  icon: React.ReactNode;
  hasUpcomingEvent?: boolean;
};

export const EvenFeedCard: React.FC<EvenFeedCardProps> = ({ children, title, width, icon, ...props }) => {
  return (
    <EvenFeedCardContainer
      {...props}
      width={width}
      flexDirection={['row', 'column', 'column']}
      justifyContent="space-between"
      alignItems="center"
    >
      <Flex flexDirection="column" height="100%">
        <h3>{title}</h3>
        {children}
      </Flex>
      <Flex alignSelf={['center', 'flex-start', 'flex-start']}>{icon}</Flex>
    </EvenFeedCardContainer>
  );
};

const SpecialEvenFeedCardContainerWrapper = styled(Flex)<{ width?: string }>`
  width: 100%;
  border-radius: 11px;
  position: relative;
  padding: 1px;
  background: #ff0083;
  box-shadow: 0px 6px 20px -4px ${rgba(COLORS.pink, 0.15)};
  background-image: conic-gradient(from var(--angle), #fff, #ff0083, #ff0083, #ff0083, #fff);
  animation: gradientRotate 6s linear infinite;

  ${MEDIA_QUERIES.tabletAndDesktop`
    width: ${props => props.width ?? '100%'};
    font-size: 1.4rem;
    gap: ${props => (props.hasUpcomingEvent ? '12px' : '28px')};
  `}
`;

export const SpecialEvenFeedCard: React.FC<EvenFeedCardProps> = ({ children, title, width, icon, ...props }) => {
  return (
    <>
      <GlobalStyle />
      <SpecialEvenFeedCardContainerWrapper width={width}>
        <Particles qtt={[20, 12, 12]} position="start" orientation="vertical" />
        <Particles qtt={[7, 12, 12]} position="end" orientation="horizontal" />
        <Particles qtt={[20, 12, 12]} position="end" orientation="vertical" />
        <Particles qtt={[7, 12, 12]} position="start" orientation="horizontal" />
        <SpecialEvenFeedCardContainer
          {...props}
          width="100%"
          flexDirection={['row', 'column', 'column']}
          justifyContent="space-between"
          alignItems="center"
        >
          <Flex flexDirection="column" height="100%">
            <h3>{title}</h3>
            {children}
          </Flex>
          <Flex alignSelf={['center', 'flex-start', 'flex-start']}>{icon}</Flex>
        </SpecialEvenFeedCardContainer>
      </SpecialEvenFeedCardContainerWrapper>
    </>
  );
};

export interface LatestQuarterlyProps {
  year: string;
  quarter: string;
  links: {
    url: string;
    label: string;
  }[];
}

export interface UpcomingEventProps {
  name: string;
  date: string;
  link: {
    url: string;
    label: string;
  };
}

const UpcomingEventIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} fill="none">
    <path
      fill="#FF0083"
      fillRule="evenodd"
      d="M5.11 1.5c.48 0 .868.388.868.867v.577h4.044v-.577a.867.867 0 1 1 1.733 0v.577h1.907c.702 0 1.271.57 1.271 1.272v9.013c0 .702-.569 1.271-1.271 1.271H2.338c-.703 0-1.272-.57-1.272-1.271V4.216c0-.702.57-1.272 1.272-1.272h1.906v-.577c0-.479.388-.867.867-.867ZM2.638 6.294c0-.18.146-.325.325-.325h2.059c.18 0 .325.145.325.325v2.058c0 .18-.146.325-.325.325H2.962a.325.325 0 0 1-.325-.325V6.294Zm.325 3.466a.325.325 0 0 0-.325.325v2.059c0 .18.146.325.325.325h2.059c.18 0 .325-.146.325-.325v-2.059a.325.325 0 0 0-.325-.325H2.962Zm3.684-3.466c0-.18.145-.325.325-.325h2.058c.18 0 .325.145.325.325v2.058c0 .18-.146.325-.325.325H6.97a.325.325 0 0 1-.325-.325V6.294ZM6.97 9.76a.325.325 0 0 0-.325.325v2.059c0 .18.145.325.325.325h2.058c.18 0 .325-.146.325-.325v-2.059a.325.325 0 0 0-.325-.325H6.97Zm3.683-3.466c0-.18.145-.325.325-.325h2.058c.18 0 .325.145.325.325v2.058c0 .18-.145.325-.325.325H10.98a.325.325 0 0 1-.325-.325V6.294Zm.325 3.466a.325.325 0 0 0-.325.325v2.059c0 .18.145.325.325.325h2.058c.18 0 .325-.146.325-.325v-2.059a.325.325 0 0 0-.325-.325H10.98Z"
      clipRule="evenodd"
    />
  </svg>
);
export const Quarterly: React.FC<{ latestQuarterly: LatestQuarterlyProps | null; hasUpcomingEvent: boolean }> = ({
  latestQuarterly,
  hasUpcomingEvent,
}) => {
  if (latestQuarterly == null) return null;
  return (
    <>
      {latestQuarterly.links.map(link => {
        const title = `${latestQuarterly.quarter} ${latestQuarterly.year}`;
        const isAudio = link.label.toLowerCase().includes('earning');
        const IconAudio = ICONS.audio;
        const IconDocument = ICONS.document;
        const width = hasUpcomingEvent ? '120px' : '100%';
        return (
          <EvenFeedCard
            key={link.url}
            title={title}
            width={width}
            role="button"
            icon={isAudio ? <IconAudio /> : <IconDocument />}
            onClick={() => {
              window.open(link.url, '_blank');
            }}
          >
            <span style={{ textTransform: 'capitalize' }}>{link.label}</span>
          </EvenFeedCard>
        );
      })}
    </>
  );
};

export const SpecialUpcomingEvent: React.FC<{
  upcomingEvent: UpcomingEventProps | null;
  hasUpcomingEvent: boolean;
}> = ({ upcomingEvent, hasUpcomingEvent }) => {
  if (upcomingEvent == null) return null;
  return (
    <>
      <SpecialEvenFeedCard
        title="Upcoming event"
        width="30%"
        role="button"
        icon={<UpcomingEventIcon />}
        onClick={() => {
          window.open(upcomingEvent.link.url, '_blank');
        }}
        hasUpcomingEvent={hasUpcomingEvent}
      >
        <Flex justifyContent="space-between" flexDirection="column">
          <Box>
            <b>{upcomingEvent.name}</b>
          </Box>
          <span style={{ textWrap: 'pretty' }}>{upcomingEvent.date}</span>
        </Flex>
      </SpecialEvenFeedCard>
    </>
  );
};

export const UpcomingEvent: React.FC<{
  upcomingEvent: UpcomingEventProps | null;
  hasUpcomingEvent: boolean;
}> = ({ upcomingEvent, hasUpcomingEvent }) => {
  if (upcomingEvent == null) return null;
  return (
    <EvenFeedCard
      title="Upcoming event"
      width="30%"
      role="button"
      icon={<UpcomingEventIcon />}
      onClick={() => {
        window.open(upcomingEvent.link.url, '_blank');
      }}
      hasUpcomingEvent={hasUpcomingEvent}
    >
      <Flex justifyContent="space-between" flexDirection="column">
        <Box>
          <b>{upcomingEvent.name}</b>
        </Box>
        <span style={{ textWrap: 'pretty' }}>{upcomingEvent.date}</span>
      </Flex>
    </EvenFeedCard>
  );
};
